(() => {
  const priceSelector = '.money[data-processed="true"][current_price]';

  const activeCurrency = () => window.Shopify?.currency?.active || window.MinimogSettings?.currency_code;

  const stripZeroDecimals = (value) => value.replace(/([.,])00(?=\s*$)/, '');

  const normalizeEgpPrices = (root = document) => {
    if (activeCurrency() !== 'EGP') return;

    root.querySelectorAll('.money, .m-price-item').forEach((price) => {
      if (price.matches('.m\\:visually-hidden, .m\\:visually-hidden *')) return;
      const visibleText = price.textContent?.replace(/\s+/g, ' ').trim() || '';
      const amountText = visibleText
        .replace(/^(?:E£|LE|EGP)\s*/i, '')
        .replace(/\s+(?:EGP|LE)$/i, '')
        .trim();

      if (!amountText || !/[0-9]/.test(amountText)) return;
      const normalized = `EGP ${stripZeroDecimals(amountText)}`;
      if (price.textContent !== normalized) {
        price.textContent = normalized;
        price.dataset.ashCurrencyNormalized = 'true';
        price.setAttribute('aria-label', normalized);
      }
    });
  };

  const normalizeConvertedPrices = (root = document) => {
    root.querySelectorAll(priceSelector).forEach((price) => {
      const visibleText = price.textContent?.replace(/\s+/g, ' ').trim() || '';
      const prefixCode = visibleText.match(/^([A-Z]{2,3})\s+/);
      const suffixCode = visibleText.match(/\s([A-Z]{3})$/);
      const amount = Number(price.getAttribute('current_price'));

      if (!prefixCode || !suffixCode || !Number.isFinite(amount)) return;

      try {
        const currencyCode = suffixCode[1];
        const fractionOptions = Number.isInteger(amount)
          ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          : {};
        const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: currencyCode === 'EGP' ? 'code' : 'narrowSymbol',
          ...fractionOptions,
        });
        const parts = formatter.formatToParts(amount);
        const currencyIndex = parts.findIndex((part) => part.type === 'currency');
        const numberIndexes = parts
          .map((part, index) => ['integer', 'group', 'decimal', 'fraction'].includes(part.type) ? index : -1)
          .filter((index) => index >= 0);
        const firstNumberIndex = numberIndexes[0];
        const lastNumberIndex = numberIndexes[numberIndexes.length - 1];
        const hasSeparatingSpace = parts
          .slice(Math.min(currencyIndex, firstNumberIndex) + 1, Math.max(currencyIndex, lastNumberIndex))
          .some((part) => part.type === 'literal' && /\s/u.test(part.value));
        const readableFormatted = parts.map((part, index) => {
          if (part.type !== 'currency' || hasSeparatingSpace) return part.value;
          return index < firstNumberIndex ? `${part.value}\u00a0` : `\u00a0${part.value}`;
        }).join('');

        if (readableFormatted && price.textContent !== readableFormatted) {
          price.textContent = readableFormatted;
          price.dataset.ashCurrencyNormalized = 'true';
          price.setAttribute('aria-label', `${readableFormatted} ${currencyCode}`);
        }
      } catch (_error) {
        // Keep the platform-provided price when the browser cannot format the currency.
      }
    });
  };

  const enhanceScrollableSizeGuides = (root = document) => {
    root.querySelectorAll('.ash-sg-scroll').forEach((region, index) => {
      const heading = region.previousElementSibling;
      const label = heading?.textContent?.replace(/\s+/g, ' ').trim() || `Size guide ${index + 1}`;
      region.tabIndex = 0;
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', `${label} table`);
    });
  };

  const enhanceEditorialMenu = (root = document) => {
    root.querySelectorAll('[data-ash-menu-close]').forEach((button) => {
      const item = button.closest('.ash-collection-menu');
      if (!item) return;

      if (button.dataset.ashMenuReady !== 'true') {
        button.dataset.ashMenuReady = 'true';
        button.addEventListener('click', () => {
          item.classList.add('ash-menu-dismissed');
          item.classList.remove('m-menu__item--active');
          item.querySelector('.m-menu__link--main')?.blur();
        });
        item.addEventListener('mouseleave', () => item.classList.remove('ash-menu-dismissed'));
        item.querySelector('.m-menu__link--main')?.addEventListener('focus', () => item.classList.remove('ash-menu-dismissed'));
      }

      if (item.dataset.ashSubmenuReady === 'true') return;

      const submenuItems = Array.from(
        item.querySelectorAll('.m-sub-menu--level-1 > .m-sub-menu__item--level-1')
      ).filter((submenuItem) => submenuItem.querySelector(':scope > .m-mega-menu__column'));

      if (!submenuItems.length) return;

      item.dataset.ashSubmenuReady = 'true';
      const activateSubmenu = (activeItem) => {
        submenuItems.forEach((submenuItem) => {
          submenuItem.classList.toggle('ash-submenu-active', submenuItem === activeItem);
        });
      };

      activateSubmenu(null);
      submenuItems.forEach((submenuItem) => {
        submenuItem.addEventListener('mouseenter', () => activateSubmenu(submenuItem));
        submenuItem.addEventListener('focusin', () => activateSubmenu(submenuItem));
      });
      item.addEventListener('mouseleave', () => activateSubmenu(null));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) activateSubmenu(null);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      enhanceScrollableSizeGuides();
      normalizeConvertedPrices();
      normalizeEgpPrices();
      enhanceEditorialMenu();
    }, { once: true });
  } else {
    enhanceScrollableSizeGuides();
    normalizeConvertedPrices();
    normalizeEgpPrices();
    enhanceEditorialMenu();
  }

  const priceObserver = new MutationObserver(() => {
    normalizeConvertedPrices();
    normalizeEgpPrices();
  });
  priceObserver.observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['data-processed', 'current_price'],
  });

  document.addEventListener('shopify:section:load', (event) => {
    enhanceScrollableSizeGuides(event.target);
    normalizeConvertedPrices(event.target);
    normalizeEgpPrices(event.target);
    enhanceEditorialMenu(event.target);
  });
})();
